import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdPosition, AdScheduleStatus, PositionScheduleItem } from '../../types/Ad.api.type';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';

/** 위치 옵션 */
const POSITION_OPTIONS: { value: AdPosition; label: string }[] = [
  { value: 'MAIN_FEED_TOP', label: '메인 상단' },
  { value: 'MAIN_FEED', label: '메인 피드' },
  { value: 'POPULAR_FEED_TOP', label: '인기설문 상단' },
  { value: 'POPULAR_FEED', label: '인기설문 피드' },
  { value: 'MY_FEED_TOP', label: '내설문 상단' },
  { value: 'MY_FEED', label: '내설문 피드' },
  { value: 'DETAIL_COMMENT', label: '상세 댓글 위' },
];

/** 상태 색상 */
const STATUS_COLORS: Record<AdScheduleStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-700 border-green-300',
  RESERVED: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  COMPLETED: 'bg-gray-100 text-gray-500 border-gray-300',
};

/** 상태 한국어 */
const STATUS_LABELS: Record<AdScheduleStatus, string> = {
  ACTIVE: '진행중',
  RESERVED: '예약',
  COMPLETED: '완료',
};

/** 타임라인 바 색상 */
const BAR_COLORS: Record<AdScheduleStatus, string> = {
  ACTIVE: 'bg-green-400',
  RESERVED: 'bg-yellow-400',
  COMPLETED: 'bg-gray-300',
};

/** 날짜 포맷 */
const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const formatDateFull = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const AdScheduleTimeline = () => {
  const [selectedPosition, setSelectedPosition] = useState<AdPosition>('MAIN_FEED');

  const { data: scheduleData, isLoading } = useQuery<PositionScheduleItem[]>({
    queryKey: ['adminAdSchedule', selectedPosition],
    queryFn: () => adApi.getPositionSchedule(selectedPosition).then(res => res.data),
  });

  const schedules = scheduleData ?? [];

  /** 타임라인 범위 계산 (현재 기준 전후 30일) */
  const now = new Date();
  const timelineStart = new Date(now);
  timelineStart.setDate(timelineStart.getDate() - 7);
  const timelineEnd = new Date(now);
  timelineEnd.setDate(timelineEnd.getDate() + 30);

  const totalMs = timelineEnd.getTime() - timelineStart.getTime();

  /** 바 위치/너비 계산 */
  const getBarStyle = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    const left = Math.max(0, ((s.getTime() - timelineStart.getTime()) / totalMs) * 100);
    const right = Math.min(100, ((e.getTime() - timelineStart.getTime()) / totalMs) * 100);
    const width = Math.max(1, right - left);
    return { left: `${left}%`, width: `${width}%` };
  };

  /** 오늘 마커 위치 */
  const todayLeft = ((now.getTime() - timelineStart.getTime()) / totalMs) * 100;

  /** 주차 눈금 생성 */
  const weekMarkers: { date: Date; left: number }[] = [];
  const cursor = new Date(timelineStart);
  cursor.setDate(cursor.getDate() - cursor.getDay()); // 일요일로 이동
  cursor.setDate(cursor.getDate() + 7); // 다음 주 시작
  while (cursor <= timelineEnd) {
    const left = ((cursor.getTime() - timelineStart.getTime()) / totalMs) * 100;
    if (left >= 0 && left <= 100) {
      weekMarkers.push({ date: new Date(cursor), left });
    }
    cursor.setDate(cursor.getDate() + 7);
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="text-base font-bold text-gray-900">예약 현황 타임라인</h3>
          <div className="flex flex-wrap gap-2">
            {POSITION_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setSelectedPosition(opt.value)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors whitespace-nowrap ${
                  selectedPosition === opt.value
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">로딩 중...</div>
        ) : schedules.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            해당 위치에 예약된 광고가 없습니다.
          </div>
        ) : (
          <div className="space-y-1 overflow-x-auto">
            {/* 타임라인 헤더 (주차 눈금) */}
            <div className="relative h-6 ml-[140px] sm:ml-[200px] border-b border-gray-200 min-w-[400px]">
              {weekMarkers.map((marker, i) => (
                <div
                  key={i}
                  className="absolute top-0 flex flex-col items-center"
                  style={{ left: `${marker.left}%`, transform: 'translateX(-50%)' }}
                >
                  <span className="text-[10px] text-gray-400">
                    {formatDate(marker.date.toISOString())}
                  </span>
                  <div className="w-px h-2 bg-gray-300" />
                </div>
              ))}
              {/* 오늘 마커 */}
              <div
                className="absolute top-0 h-full flex flex-col items-center"
                style={{ left: `${todayLeft}%`, transform: 'translateX(-50%)' }}
              >
                <span className="text-[10px] font-bold text-red-500">오늘</span>
                <div className="w-px h-2 bg-red-400" />
              </div>
            </div>

            {/* 각 예약 바 */}
            {schedules.map(item => (
              <div key={item.positionId} className="flex items-center gap-0 group">
                {/* 광고 정보 */}
                <div className="w-[140px] sm:w-[200px] flex-shrink-0 flex items-center gap-2 pr-3 py-2">
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[item.status]}`}>
                    {STATUS_LABELS[item.status]}
                  </Badge>
                  <span className="text-xs text-gray-700 truncate flex-1" title={item.adTitle}>
                    {item.adTitle}
                  </span>
                </div>

                {/* 타임라인 바 영역 */}
                <div className="flex-1 relative h-8 bg-gray-50 rounded">
                  {/* 오늘 선 */}
                  <div
                    className="absolute top-0 h-full w-px bg-red-300 z-10"
                    style={{ left: `${todayLeft}%` }}
                  />
                  {/* 바 */}
                  <div
                    className={`absolute top-1 h-6 rounded ${BAR_COLORS[item.status]} opacity-80 hover:opacity-100 transition-opacity cursor-default`}
                    style={getBarStyle(item.startDate, item.endDate)}
                    title={`${item.adTitle}\n${formatDateFull(item.startDate)} ~ ${formatDateFull(item.endDate)}`}
                  >
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-medium truncate px-1">
                      {formatDate(item.startDate)}~{formatDate(item.endDate)}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* 범례 */}
            <div className="flex gap-4 pt-3 ml-[140px] sm:ml-[200px] border-t border-gray-100">
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded ${BAR_COLORS[key as AdScheduleStatus]}`} />
                  <span className="text-xs text-gray-500">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdScheduleTimeline;
